import { inject, Injectable, Injector, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TokenService } from './token.service';
import { ApiResponse } from '../models/product.models';
import {
  Account,
  AuthResponseData,
  AuthUserDto,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UserProfile,
} from '../models/auth.models';
import { jwtTenantId } from '../utils/jwt.util';
import { CurrencyService } from './currency.service';
import { ApiCacheService } from '../cache/api-cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly endPoint = environment.apiUrl + '/api/auth';

  private http = inject(HttpClient);
  private router = inject(Router);
  private tokenService = inject(TokenService);
  /**
   * `CurrencyService` DARHOL emas, KERAK BO'LGANDA olinadi.
   *
   * `AuthService` ni `authInterceptor` ham inject qiladi, ya'ni u HTTP zanjiri
   * ichida quriladi. Maydonda to'g'ridan-to'g'ri `inject(CurrencyService)`
   * yozilsa, bu qurilish paytida yana bitta HTTP-bog'liq servis tortiladi.
   * Injector orqali kechiktirish shu bog'lanishni butunlay yo'q qiladi va
   * hech narsani buzmaydi — narxi nol.
   */
  private injector = inject(Injector);

  /**
   * To'g'ridan-to'g'ri olinadi: `CurrencyService` dan farqli, bu servis HTTP'ga
   * bog'liq emas (faqat IndexedDB va token), shuning uchun interceptor zanjiriga
   * hech narsa tortmaydi.
   */
  private apiCache = inject(ApiCacheService);

  /** Valyuta sozlamasi qaysi tenant uchun yuklangani — takroriy so'rovni to'xtatadi. */
  private loadedCurrencyTenantId: string | null = null;

  private _user = signal<Account | null>(null);

  user = this._user.asReadonly();
  currentUser = this._user.asReadonly();
  isLoggedIn = computed(() => !!this._user());
  isAuthenticated = computed(() => !!this._user());
  isSystemUser = computed(() => this._user()?.isSystemUser ?? false);

  constructor() {
    this.initialLoad();
  }

  private initialLoad(): void {
    const accountStr = localStorage.getItem('user_account');
    if (accountStr && this.tokenService.hasToken()) {
      try {
        this._user.set(JSON.parse(accountStr) as Account);
      } catch {
        this.logout();
      }
    }
  }

  pendingBusinessCount(): number {
    try {
      const raw = localStorage.getItem('bepos_pending_businesses');
      const arr = raw ? (JSON.parse(raw) as unknown[]) : [];
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  }

  clearPendingBusinesses(): void {
    localStorage.removeItem('bepos_pending_businesses');
  }

  login(credentials: LoginRequest): Observable<ApiResponse<AuthResponseData>> {
    return this.http
      .post<ApiResponse<AuthResponseData>>(`${this.endPoint}/login`, credentials)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.handleAuthSuccess(response.data);
          }
        }),
      );
  }

  selectBusiness(userId: string, businessId: string): Observable<ApiResponse<AuthResponseData>> {
    return this.http
      .post<ApiResponse<AuthResponseData>>(`${this.endPoint}/select-business`, {
        userId,
        businessId,
      })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.handleAuthSuccess(response.data);
          }
        }),
      );
  }

  register(data: RegisterRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.endPoint}/register`, data);
  }

  refreshToken(): Observable<ApiResponse<AuthResponseData>> {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) return throwError(() => new Error('No refresh token available'));

    return this.http
      .post<ApiResponse<AuthResponseData>>(`${this.endPoint}/refresh`, { refreshToken })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.handleAuthSuccess(response.data);
          } else {
            this.logout();
          }
        }),
        catchError((err) => {
          this.logout();
          return throwError(() => err);
        }),
      );
  }

  logout(): void {
    // Boshqa hisobga kirilsa valyuta qaytadan yuklanishi kerak.
    this.loadedCurrencyTenantId = null;
    // Keshdagi lug'atlar (do'konlar va h.k.) shu hisobga tegishli — keyingi
    // foydalanuvchi ularni ko'rmasligi kerak. Kutilmaydi: chiqish sahifani
    // yangilamaydi, shuning uchun tozalash orqa fonda tugaydi.
    void this.apiCache.clearAll();
    this.tokenService.removeTokens();
    localStorage.removeItem('user_account');
    this.clearPendingBusinesses();
    this._user.set(null);
    void this.router.navigate(['/login']);
  }

  getAccessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  getMyProfile(): Observable<ApiResponse<UserProfile>> {
    const userId = this._user()?.id;
    return this.http
      .get<ApiResponse<UserProfile>>(`${this.endPoint}/my-profile`, {
        params: userId ? { userId } : {},
      })
      .pipe(
        tap((res) => {
          if (res.success && res.data) {
            const d = res.data;
            const updated: Account = {
              id: d.id,
              fullName: d.fullName,
              phoneNumber: d.phoneNumber,
              gender: d.gender,
              role: d.role,
              avatar: d.avatar,
              isSystemUser: d.isSystemUser,
              permissions: this._user()?.permissions,
            };
            this._user.set(updated);
            localStorage.setItem('user_account', JSON.stringify(updated));
          }
        }),
      );
  }

  forgotPassword(data: ForgotPasswordRequest): Observable<ApiResponse<{ message?: string }>> {
    return this.http.post<ApiResponse<{ message?: string }>>(
      `${this.endPoint}/forgot-password`,
      data,
    );
  }

  resetPassword(data: ResetPasswordRequest): Observable<ApiResponse<{ message?: string }>> {
    return this.http.post<ApiResponse<{ message?: string }>>(
      `${this.endPoint}/reset-password`,
      data,
    );
  }

  uploadAvatar(file: File): Observable<ApiResponse<{ avatarUrl: string }>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.put<ApiResponse<{ avatarUrl: string }>>(`${this.endPoint}/avatar`, form).pipe(
      tap((res) => {
        if (res.success && res.data?.avatarUrl) {
          const updated = { ...this._user()!, avatar: res.data.avatarUrl };
          this._user.set(updated);
          localStorage.setItem('user_account', JSON.stringify(updated));
        }
      }),
    );
  }

  deleteAvatar(): Observable<unknown> {
    return this.http.delete(`${this.endPoint}/avatar`).pipe(
      tap(() => {
        const updated = { ...this._user()!, avatar: undefined };
        this._user.set(updated);
        localStorage.setItem('user_account', JSON.stringify(updated));
      }),
    );
  }

  private mapUserToAccount(u: AuthUserDto): Account {
    const role = u.role ?? '';
    return {
      id: u.id,
      fullName: u.fullName,
      phoneNumber: u.phoneNumber,
      gender: 0,
      role,
      permissions: u.permissions ?? undefined,
      isSystemUser: role === 'SuperAdmin' || role === 'Admin' || role === 'Operator',
    };
  }

  private handleAuthSuccess(data: AuthResponseData | null | undefined): void {
    if (!data?.accessToken || !data.refreshToken || !data.user) {
      return;
    }
    this.tokenService.saveAccessToken(data.accessToken);
    this.tokenService.saveRefreshToken(data.refreshToken);
    const account = this.mapUserToAccount(data.user);
    this._user.set(account);
    localStorage.setItem('user_account', JSON.stringify(account));
    if (data.businesses && data.businesses.length > 1) {
      localStorage.setItem('bepos_pending_businesses', JSON.stringify(data.businesses));
    } else if (jwtTenantId(data.accessToken)) {
      this.clearPendingBusinesses();
    }

    // Valyuta sozlamalari TENANT'ga bog'liq, tokenga emas. `handleAuthSuccess`
    // jimgina token yangilashda ham chaqiriladi (401 → refresh), shuning uchun
    // tenant o'zgarganidagina yuklaymiz — aks holda har yangilanishda ortiqcha
    // so'rov ketardi, ustiga refresh navbati ishlayotgan paytda.
    const tenantId = jwtTenantId(data.accessToken);
    if (tenantId && tenantId !== this.loadedCurrencyTenantId) {
      this.loadedCurrencyTenantId = tenantId;
      this.injector.get(CurrencyService).load().subscribe();
    }
  }
}
